import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ExternalLink, Check, Lock, CreditCard, Wallet, Download, ArrowRight,
} from "lucide-react";
import { PILL } from "./shared";

/**
 * Step-by-step Stripe Connect onboarding wizard + transactions list.
 *
 * Three implicit steps that the page renders contextually:
 *   1. Create Connect Express account (`stripe-connect/onboard` POST)
 *   2. Finish verification (same endpoint; Stripe resumes the flow)
 *   3. Payments live → open Stripe dashboard
 */
function StepIndicator({ step }) {
  const items = [
    { n: 1, label: "Create account" },
    { n: 2, label: "Verify identity" },
    { n: 3, label: "Go live" },
  ];
  return (
    <ol className="flex items-center gap-3 mb-5" data-testid="connect-step-indicator">
      {items.map((it, i) => {
        const state = step > it.n ? "done" : step === it.n ? "active" : "future";
        return (
          <li key={it.n} className="flex items-center gap-3 text-sm">
            <span
              className={`w-7 h-7 rounded-full grid place-items-center font-extrabold border-2 ${
                state === "done"
                  ? "bg-[#10B981] text-white border-[#10B981]"
                  : state === "active"
                  ? "bg-black text-white border-black"
                  : "bg-white text-gray-400 border-gray-300"
              }`}
              data-testid={`connect-step-${it.n}-${state}`}
            >
              {state === "done" ? <Check className="w-3.5 h-3.5" /> : it.n}
            </span>
            <span className={state === "future" ? "text-gray-400" : "font-bold"}>{it.label}</span>
            {i < items.length - 1 && <ArrowRight className={`w-4 h-4 ${state === "future" ? "text-gray-300" : "text-gray-500"}`} />}
          </li>
        );
      })}
    </ol>
  );
}

export default function PaymentsTab({ biz }) {
  const [status, setStatus] = useState(null);
  const [orders, setOrders] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [refundingId, setRefundingId] = useState(null);

  const load = async () => {
    try {
      const [{ data: s }, { data: o }] = await Promise.all([
        api.get("/ecommerce/stripe-connect/status"),
        api.get("/ecommerce/orders"),
      ]);
      setStatus(s); setOrders(o);
    } catch { toast.error("Couldn't load payments"); }
  };
  useEffect(() => { load(); }, []);

  const onboard = async () => {
    setOnboarding(true);
    try {
      const { data } = await api.post("/ecommerce/stripe-connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.onboard_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start onboarding");
      setOnboarding(false);
    }
  };

  const openDashboard = async () => {
    try {
      const { data } = await api.get("/ecommerce/stripe-connect/login-link");
      window.open(data.url, "_blank", "noopener");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open Stripe dashboard"); }
  };

  const refund = async (order) => {
    if (!window.confirm(`Refund ${order.product_name} (${(order.amount_total / 100).toFixed(2)} ${(order.currency || "usd").toUpperCase()})?`)) return;
    setRefundingId(order.order_id);
    try {
      await api.post(`/ecommerce/orders/${order.order_id}/refund`, { reason: "requested_by_customer" });
      toast.success("Refund issued");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
    setRefundingId(null);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/ecommerce/orders/export.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${biz?.slug || "orders"}-orders.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't export orders"); }
  };

  if (!status) return <div className="nb-card-flat p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  const fmt = (cents) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Derive which wizard step the merchant is on. Used by the step indicator
  // and the body state below.
  const wizardStep = !status.connected ? 1 : !status.charges_enabled ? 2 : 3;

  return (
    <div data-testid="payments-tab">
      <div className="nb-card p-5 mb-6">
        <div className="font-display text-2xl font-extrabold flex items-center gap-2"><Wallet className="w-6 h-6" /> Payments</div>
        <div className="text-sm text-gray-600 mt-1">Accept payments from customers. Funds go directly to your Stripe account, minus our small platform fee.</div>
      </div>

      {status.connect_supported && <StepIndicator step={wizardStep} />}

      {!status.connect_supported ? (
        <div className="nb-card p-6 bg-[#FFF7E6]" data-testid="connect-unavailable">
          <div className="font-display text-xl font-extrabold mb-1 flex items-center gap-2"><Lock className="w-5 h-5" /> Stripe Connect not configured</div>
          <p className="text-sm text-gray-700">The platform needs a real Stripe secret key (sk_test_… or sk_live_…) to enable customer payouts. Once added, you&apos;ll be able to onboard your own Stripe account in a few clicks.</p>
        </div>
      ) : !status.connected ? (
        <div className="nb-card p-6" data-testid="connect-not-started">
          <CreditCard className="w-10 h-10 mb-3 text-[#8B5CF6]" />
          <div className="font-display text-2xl font-extrabold mb-1">Set up payments</div>
          <p className="text-gray-700 mb-4">Connect your own Stripe account to start accepting payments. Setup takes ~3 minutes. Funds go directly to you.</p>
          <ul className="text-sm text-gray-700 space-y-2 mb-5">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Accept cards, Apple Pay, Google Pay, Link — all included</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Payouts settle to your bank, typically every 2 days</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> Platform fee of 5% per transaction on top of standard Stripe fees</li>
          </ul>
          <button onClick={onboard} disabled={onboarding} className={`${PILL} bg-[#635BFF] text-white`} data-testid="connect-onboard-btn">
            {onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4" /> Connect with Stripe</>}
          </button>
        </div>
      ) : !status.charges_enabled ? (
        <div className="nb-card p-6 bg-[#FFF7E6]" data-testid="connect-in-progress">
          <div className="font-display text-xl font-extrabold mb-1">Finish setting up payments</div>
          <p className="text-sm text-gray-700 mb-3">Stripe needs a few more details before you can accept payments. Click below to complete onboarding.</p>
          {(status.requirements_currently_due || []).length > 0 && (
            <div className="text-xs text-gray-600 mb-3">Still needed: {(status.requirements_currently_due || []).join(", ")}</div>
          )}
          <button onClick={onboard} disabled={onboarding} className={`${PILL} bg-[#635BFF] text-white`} data-testid="connect-continue-btn">
            {onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4" /> Continue setup</>}
          </button>
        </div>
      ) : (
        <div className="nb-card p-6 bg-[#F0FDF4]" data-testid="connect-active">
          <div className="flex items-center gap-2 mb-2"><Check className="w-5 h-5 text-[#10B981]" /><span className="font-display text-xl font-extrabold">Payments are live</span></div>
          <p className="text-sm text-gray-700 mb-3">Customers can buy from your website. Funds settle in your Stripe account minus the platform fee.</p>
          <button onClick={openDashboard} className={`${PILL} bg-white`} data-testid="connect-dashboard-btn">
            <ExternalLink className="w-4 h-4" /> Open Stripe dashboard
          </button>
        </div>
      )}

      {orders && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500 px-1">Recent orders</div>
            {(orders.orders || []).length > 0 && (
              <button onClick={exportCsv} className={`${PILL} bg-white text-xs`} data-testid="export-orders-csv">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            )}
          </div>
          {(orders.orders || []).length === 0 ? (
            <div className="nb-card-flat p-6 text-center text-sm text-gray-600">No orders yet.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="nb-card-flat p-4 text-center"><div className="text-xs text-gray-500 font-bold">Total orders</div><div className="text-2xl font-extrabold mt-1">{orders.summary.total_count}</div></div>
                <div className="nb-card-flat p-4 text-center bg-[#F0FDF4]"><div className="text-xs text-gray-500 font-bold">Paid</div><div className="text-2xl font-extrabold mt-1">{orders.summary.paid_count}</div></div>
                <div className="nb-card-flat p-4 text-center bg-[#FFEEDD]"><div className="text-xs text-gray-500 font-bold">Revenue</div><div className="text-2xl font-extrabold mt-1">{fmt(orders.summary.total_revenue_cents)}</div></div>
              </div>
              <div className="space-y-2" data-testid="orders-list">
                {orders.orders.slice(0, 20).map((o) => (
                  <div key={o.order_id} className="nb-card p-3 flex items-center gap-3" data-testid={`order-${o.order_id}`}>
                    <div className={`w-2 h-2 rounded-full ${o.payment_status === "paid" ? "bg-[#10B981]" : o.payment_status === "refunded" ? "bg-amber-500" : "bg-gray-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{o.product_name}</div>
                      <div className="text-xs text-gray-600 truncate">{o.customer_email || "—"} · {new Date(o.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-extrabold">{fmt(o.amount_total || 0)}</div>
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500">{o.payment_status}</div>
                    </div>
                    {o.payment_status === "paid" && !o.refunded_at && o.kind !== "subscription" && (
                      <button onClick={() => refund(o)} disabled={refundingId === o.order_id}
                        className={`${PILL} bg-white text-xs`} data-testid={`refund-${o.order_id}`}>
                        {refundingId === o.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refund"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
